import React, {useState} from 'react';
import {Facility, FacilityData} from "types/Facility";
import {Form, Formik, FormikHelpers, FormikProps} from "formik";
import Field from "components/Form/Field";
import Selector from "components/Form/Selector";
import Locator from "components/Form/Locator";
import {useQuery} from "react-query";
import ngage from "services/ngage";
import IconName from "types/IconName";
import {Zone} from "types/Zone";
import Toggle from "components/Form/Toggle";
import {useOperator} from "context/OperatorContext";
import FormControls from "components/FormControls";
import Visibility from "types/Visibility";
import InformationListField from "components/Form/InformationEditor";
import Uploader from "components/Form/Uploader";
import AccessibilitySelector from "components/Form/AccessibilitySelector/AccessibilitySelector";
import {OpeningHoursFieldSet} from "../../../components/Form/Form/Fieldset";
import LinkEditor from "../../../components/Form/LinkEditor";
import {useAuth} from "../../../context/AuthContext";
import {Geometry} from "geojson";
import * as Yup from "yup";
import {urlTest} from "../../../services/validation-schemas";
import {ErrorNotification} from "../../../components/Form/ErrorNotification";
import FieldHelp from "../../../components/Form/Form/Field/FieldHelp";
import {ExclamationCircleIcon} from "@heroicons/react/solid";
import {ExhibitData} from "../../../types/Exhibit";
import {Building, BuildingData} from "../../../types/Building";
import {Floor, FloorData} from "../../../types/Floor";
import {toTitleCase} from "../../../services/string";
import FeatureFlag from "../../../types/FeatureFlag";
import {TextAreaField} from "../../../components/Form/Form";

// Types ----

type FacilityFormProps = {
  facility?: Facility
  onSubmit: (values: FacilityData, helpers: FormikHelpers<FacilityData>) => Promise<any>
}

// Helpers ----


// TODO: Make this reusable
function buildingAsItem(building: Building) {
  return {
    id: building.id || 0,
    name: building.name,
    icon: IconName.OFFICE_BUILDING
  }
}

// TODO: Make this reusable
function floorAsItem(floor: Floor) {
  return {
    id: floor.id || 0,
    name: floor.name,
    description: floor.building.name,
    icon: IconName.CHEVRON_UP_DOWN
  }
}

// TODO: Make this reusable
function zoneAsItem(zone: Zone) {
  return {
    id: zone.id || 0,
    name: zone.name,
    icon: IconName.CUBE_TRANSPARENT
  }
}

// Component ----

function FacilityForm({facility, onSubmit}: FacilityFormProps) {
  const {termFor} = useOperator();
  const auth = useAuth();

  const [hasAction, setHasAction] = useState(!!facility?.action);
  const [mapFlyToGeometry, setMapFlyToGeometry] = useState<Geometry | undefined>();
  const [selectedBuildingId, setSelectedBuildingId] = useState<number | undefined>(facility?.location?.building?.id);
  const [selectedFloorId, setSelectedFloorId] = useState<number | undefined>(facility?.location?.id);

  const {data: facilityTypes, isLoading: typesLoading} = useQuery(
    ['facility-types.all', {page: 1}],
    () => ngage.facilityTypes.all(1),
    {enabled: auth.can('facility-types:read')}
  );

  const {data: zones, isLoading: zonesLoading} = useQuery(
    ['zones.all', {page: 1, floors: selectedFloorId}],
    () => ngage.zones.all({page: 1, floors: selectedFloorId}),
    {enabled: auth.can('zones:read')}
  )

  const {data: buildings, isLoading: buildingsLoading} = useQuery(
    ['buildings.filtered'],
    () => ngage.buildings.filtered(),
    {enabled: auth.can('buildings:read')}
  )

  const {data: floors, isLoading: floorsLoading} = useQuery(
    ['floors.filtered', { building: selectedBuildingId}],
    () => ngage.floors.filtered({ building: selectedBuildingId}),
    {enabled: auth.can('floors:read')}
  )

  const selectableFacilityTypes = facilityTypes?.data.map(facilityType => ({
    id: facilityType.id,
    name: facilityType.name,
    description: facilityType.description,
    icon: facilityType.icon,
  })) || []


  function toggleAction(enabled: boolean, {setFieldValue}: FormikHelpers<any>) {
    void setFieldValue('action', enabled ? {text: '', url: ''} : null)
    setHasAction(enabled)
  }

  function toggleIndoor(isIndoor: boolean, {setFieldValue}: FormikProps<ExhibitData>) {
    void setFieldValue('is_indoor', isIndoor);
    void setFieldValue('position', null);
    void setFieldValue('zone_id', null);

    if (!isIndoor) {
      void setFieldValue('location_id', null);
      void setFieldValue('location_type', null);
      void setFieldValue('building_id', null);

      setSelectedBuildingId(undefined);
      setSelectedFloorId(undefined);
    }
  }

  function selectBuilding(buildingId: number, formik: FormikHelpers<BuildingData>) {
    setSelectedBuildingId(buildingId);
    void formik.setFieldValue('building_id', buildingId).then(_ => {
      void formik.validateField('building_id');
      void formik.validateField('location_id');
    });
    void formik.setFieldValue('position', null);
  }

  function selectFloor(floorId: number, formik: FormikHelpers<FloorData>) {
    setSelectedFloorId(floorId);
    void formik.setFieldValue('location_type', 'floor');
    void formik.setFieldValue('location_id', floorId).then(_ => {
      void formik.validateField('location_id')
    });
    void formik.setFieldValue('position', null);
  }

  function selectZone(zoneId: number | string, formik: FormikHelpers<any>) {
    void formik.setFieldValue('zone_id', zoneId).then(_ => {
      void formik.validateField('zone_id')
    });
    void formik.setFieldValue('position', null);

    // Find the zone and fly to it on the map
    const zone = zones?.data.find(zone => zone.id === Number(zoneId));
    setMapFlyToGeometry(zone?.area || facility?.position);
  }

  const canUseBuildings = auth.canUse(FeatureFlag.IndoorMapping);

  const validationSchema = Yup.object().shape({
    type_id: Yup.number().required('This field is required'),
    name: Yup.string().required("This field is required").max(255, "Maximum of 255 characters"),
    description: Yup.string().max(255, "Maximum character limit exceeded"),
    zone_id: Yup.number().required('This field is required'),
    building_id:
      Yup.number().nullable().when('is_indoor', ([isIndoor]: any, schema: any) => {
        if (!isIndoor) return schema;
        return schema.required('This field is required');
      }),
    location_id:
      Yup.number().nullable().when(['is_indoor', 'building_id'], ([isIndoor, buildingId]: any, schema: any) => {
        if (!isIndoor) return schema;
        return schema.required(!!buildingId ? 'This field is required' : 'Please select a building first');
      }),
    action: Yup.object().shape({
      text: Yup.string().required('This field is required').max(100, 'Maximum 100 characters'),
      url: Yup.string()
        .required('This field is required')
        .max(150, 'Maximum 150 characters')
        .test('valid-url', urlTest),
    }).nullable(),
    information: Yup.array().of(Yup.object().shape({
      name: Yup.string().required("This field is required").max(255, 'Maximum of 255 characters'),
      content: Yup.string().required("This field is required").max(50000, 'Maximum character limit exceeded'),
    })),
    position: Yup.object().required(`Please place the ${termFor("facility")} on the map using the drawing tool in the top left corner`),
  })

  const initialValues: FacilityData = {
    type_id: facility?.type.id,
    name: facility?.name || '',
    description: facility?.description || '',
    accessibility_criteria: facility?.accessibility_criteria || [],
    is_sustainable: facility?.is_sustainable ?? false,
    is_destination: facility?.is_destination ?? true,
    hours: facility?.hours || null,
    action: facility?.action || null,
    information: facility?.information.map(section => ({
      id: section.id,
      name: section.name,
      content: section.content
    })) || [],
    media: facility?.media.map(file => ({id: file.id, visibility: Visibility.EVERYWHERE})) || [],
    zone_id: facility?.zone.id,
    position: facility?.position || undefined,
    is_mapped: facility?.is_mapped ?? true,
    is_pinned: facility?.is_pinned ?? true,
    is_published: facility?.is_published ?? true,
    location_type: facility?.location?.type,
    location_id: facility?.location?.id,

    // Not required in facility payload
    building_id: facility?.location?.building?.id,
    is_indoor: facility?.location?.type === 'building' || facility?.location?.type === 'floor' || facility?.location?.type === 'room',
  }

  return (
    <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={onSubmit}>
      {formik => {
        return (
          <Form className="space-y-8 divide-y divide-gray-200">
            <ErrorNotification formik={formik} />
  
            <div className="shadow sm:rounded-md">
              <div className="px-4 py-5 bg-white sm:p-6 sm:rounded-md">
                <div className="space-y-8 divide-y divide-gray-200">
                  <div>
                    <div>
                      <h3 className="text-lg leading-6 font-medium text-gray-900">
                        Key Information
                      </h3>
                      <p className="mt-1 text-sm text-gray-500">
                        Basic details about the {termFor('facility')}
                      </p>
                    </div>
                    <div className="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                      <div className="sm:col-span-2">
                        <Selector
                          label={termFor('Facility Type *')}
                          isLoading={typesLoading}
                          items={selectableFacilityTypes}
                          selectedId={initialValues.type_id}
                          onChange={item => formik.setFieldValue('type_id', item.id)}
                        />
  
                        {(!!formik.errors.type_id && !!formik.touched.type_id) &&
                          <FieldHelp isError>
                            {formik.errors.type_id}
                          </FieldHelp>
                        }
                      </div>
  
                      <div className="sm:col-span-3">
                        <Field
                          type="text"
                          label="Name *"
                          hint='255 chars max'
                          name="name"
                          id="name"
                          autoComplete="off"
                          maxLength="255"
                          autoFocus
                        />
                      </div>
  
                      <div className="sm:col-span-6">
                        <TextAreaField
                          type="text"
                          label="Description"
                          hint="Optional. 255 chars max."
                          name="description"
                          id="description"
                          autoComplete="off"
                          maxChars={255}
                          rows={3}
                        />
                      </div>
  
                      {/* Link */}
                      <div className="sm:col-span-6">
                        <Toggle
                          label="Action"
                          checked={hasAction}
                          onChange={checked => toggleAction(checked, formik)}
                        />
  
                        {hasAction &&
                          <div className="mt-2">
                            <LinkEditor name="action" />
                          </div>
                        }
                      </div>
  
                      {/* Accessibility */}
                      <div className="sm:col-span-6">
                        <AccessibilitySelector
                          selectedIds={initialValues.accessibility_criteria}
                          onChange={criteria => formik.setFieldValue('accessibility_criteria', criteria)}
                        />
                      </div>
  
                      {/* Sustainability */}
                      <div className="sm:col-span-6">
                        <Toggle
                          label="Sustainable"
                          description={`Whether this ${termFor('facility')} is environmentally and socially sustainable`}
                          checked={formik.values.is_sustainable}
                          onChange={checked => formik.setFieldValue('is_sustainable', checked)}
                        />
                      </div>
                    </div>
                  </div>
  
                  <div className="pt-8">
                    <div>
                      <h3 className="text-lg leading-6 font-medium text-gray-900">
                        Opening Hours
                      </h3>
                      <p className="mt-1 text-sm text-gray-500">
                        When the facility is open for use
                      </p>
                    </div>
                    <div className="mt-6">
                      <OpeningHoursFieldSet name="hours" canInherit/>
                    </div>
                  </div>
  
                  <div className="pt-8">
                    <div>
                      <h3 className="text-lg leading-6 font-medium text-gray-900">
                        Information
                      </h3>
                      <p className="mt-1 text-sm text-gray-500">
                        The main sections of information about this {termFor('facility')}
                      </p>
                    </div>
                    <div className="mt-6">
                      <InformationListField name="information" information={formik.values.information}/>
                    </div>
                  </div>
  
                  <div className="pt-8">
                    <div>
                      <h3 className="text-lg leading-6 font-medium text-gray-900">
                        Media
                      </h3>
                      <p className="mt-1 text-sm text-gray-500">
                        Images, audio and video that's relevant to this {termFor('facility')}
                      </p>
                    </div>
                    <div className="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                      <div className="sm:col-span-6">
                        <Uploader
                          type={[Uploader.types.IMAGE, Uploader.types.AUDIO, Uploader.types.VIDEO, Uploader.types.DOCUMENT]}
                          files={facility?.media || []}
                          onChange={files => {
                            void formik.setFieldValue('media', files.map(file => ({id: file.id})))
                          }}
                        />
                      </div>
                    </div>
                  </div>
  
                  {/* Location */}
                  <div className="pt-8">
                    <div>
                      <h3 className="text-lg leading-6 font-medium text-gray-900">
                        Location
                      </h3>
                      <p className="mt-1 text-sm text-gray-500">
                        Set where the {termFor('facility')} is located
                      </p>
                    </div>
  
                    {canUseBuildings &&
                      <>
                        <div className="mt-7">
                          <Toggle
                            label="Indoor"
                            description={`This ${termFor('facility')} is inside a building`}
                            multiline
                            checked={formik.values.is_indoor}
                            onChange={enabled => toggleIndoor(enabled, formik)}
                          />
                        </div>
  
                        {formik.values.is_indoor &&
                          <div className="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                            <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6 sm:col-span-6">
                              <div className="sm:col-span-3">
                                <Selector
                                  label={`${toTitleCase(termFor('Building'))} *`}
                                  isLoading={buildingsLoading}
                                  items={buildings?.data.map(buildingAsItem) || []}
                                  selectedId={formik.values.building_id}
                                  onChange={building => selectBuilding(building.id, formik)}
                                  disabled={!buildings}
                                />
  
                                {(!!formik.errors.building_id && !!formik.touched.building_id) &&
                                  <FieldHelp isError>
                                    {formik.errors.building_id}
                                  </FieldHelp>
                                }
                              </div>
                            </div>
  
                            <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6 sm:col-span-6">
                              <div className="sm:col-span-3">
                                <Selector
                                  label={`${toTitleCase(termFor('Floor'))} *`}
                                  isLoading={floorsLoading}
                                  items={floors?.data.map(floorAsItem) || []}
                                  selectedId={formik.values.location_id}
                                  onChange={floor => selectFloor(floor.id, formik)}
                                  disabled={!formik.values.building_id}
                                />
  
                                {(!!formik.errors.location_id && !!formik.touched.location_id) &&
                                  <FieldHelp isError>
                                    {formik.errors.location_id}
                                  </FieldHelp>
                                }
                              </div>
                            </div>
                          </div>
                        }
                      </>
                    }
  
                    <div className="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                      <div className="sm:col-span-3">
                        <Selector
                          label={termFor('Zone *')}
                          isLoading={zonesLoading}
                          items={zones?.data.map(zoneAsItem) || []}
                          selectedId={initialValues.zone_id}
                          onChange={zone => selectZone(zone.id, formik)}
                          disabled={formik.values.is_indoor && !formik.values.location_id}
                        />
  
                        {(!!formik.errors.zone_id && !!formik.touched.zone_id) &&
                          <FieldHelp isError>
                            {formik.errors.zone_id}
                          </FieldHelp>
                        }
                      </div>
  
                      <div className="sm:col-span-6 rounded-md overflow-hidden">
                        <span className="block text-sm font-medium text-gray-700">
                            Position
                        </span>
                        <div className="mt-1">
                          <Locator
                            height={'32rem'}
                            type='position'
                            geometry={initialValues.position}
                            flyToGeometry={mapFlyToGeometry}
                            onChange={geometry => formik.setFieldValue('position', geometry)}
                            buildingId={formik.values.building_id}
                            floorId={formik.values.location_id}
                          />
                        </div>
                        <p className="mt-2 text-sm text-gray-500">
                          Place the {termFor('facility')} on the map using the drawing tool
                        </p>
  
                        {(!!formik.errors.position && formik.touched.position) &&
                          <FieldHelp isError>
                            <div className="flex items-center gap-2">
                              <ExclamationCircleIcon className="h-5 w-5 text-red-500" aria-hidden="true"/>
                              {formik.errors.position}
                            </div>
                          </FieldHelp>
                        }
                      </div>
                    </div>
                  </div>
  
                  {/* Mapping */}
                  <div className="pt-8">
                    <div>
                      <h3 className="text-lg leading-6 font-medium text-gray-900">
                        Mapping
                      </h3>
                      <p className="mt-1 text-sm text-gray-500">
                        Choose how visible this {termFor('facility')} on the map
                      </p>
                    </div>
                    <div className="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                      <div className="sm:col-span-3">
                        <Toggle
                          label="Show position"
                          checked={formik.values.is_mapped}
                          onChange={checked => {
                            void formik.setFieldValue('is_mapped', checked)
                            void formik.setFieldValue('is_pinned', checked);
                            void formik.setFieldValue('is_destination', checked);
                          }}
                        />
                      </div>
  
                      {formik.values.is_mapped &&
                        <>
                          <div className="sm:col-span-3">
                            <Toggle
                              label="Show pin"
                              checked={formik.values.is_pinned}
                              onChange={checked => formik.setFieldValue('is_pinned', checked)}
                            />
                          </div>
  
                          <div className="sm:col-span-3">
                            <Toggle
                              label="Directions"
                              description={`Allow visitors to request directions to this ${termFor('facility')}`}
                              multiline
                              checked={formik.values.is_destination}
                              onChange={checked => formik.setFieldValue('is_destination', checked)}
                            />
                          </div>
                        </>
                      }
                    </div>
                  </div>
  
                  {/* Visibility */}
                  <div className="pt-8">
                    <div>
                      <h3 className="text-lg leading-6 font-medium text-gray-900">
                        Visibility
                      </h3>
                      <p className="mt-1 text-sm text-gray-500">
                        Set the visibility of this {termFor('facility')}
                      </p>
                    </div>
                    <div className="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
  
                      <div className="sm:col-span-6">
                        <Toggle
                          label="Publish"
                          description={`Makes this ${termFor('facility')} appear in the audience app, and makes it selectable in other parts of the operator app`}
                          checked={formik.values.is_published}
                          onChange={checked => formik.setFieldValue('is_published', checked)}
                          multiline
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
  
              <FormControls formik={formik}/>
            </div>
          </Form>
        )
      }}
    </Formik>
  );
}

export default FacilityForm;